// import external libraries
import { useState } from 'react';
import { useDebounceValue } from 'usehooks-ts';

// import data
import { queries } from 'RestAPIs/rapini';

// import utils
import { formatEntityDisplayLabel } from 'components/mrp/mrp.utils';

// import components
import BacklogFilterMultiSelect from '../BacklogFilterMultiSelect';

// import constants
import { BACKLOG_ENTITY_PAGE_SIZE } from '../../../../Backlog.consts';

// import types
import type { PlanningEntityType, SchedulingLabel } from 'generated/index';
import type { BacklogEntityFilter } from '../../../../Backlog.types';
import type { BacklogEntitySearchProps } from './BacklogEntitySearch.types';

// The backend only queries an entity source when the label set carries an internal or external label —
// with none it returns nothing. MRP is internal (like the shared picker's default), which reaches the
// SDB-planning entities the takenlijst works with.
const SCHEDULING_LABEL = 'MRP';

/**
 * One field that searches the tenant for a single entity type and lets the planner pick any number of
 * them. Unlike the shared afspraken-planning picker it is not always-open and holds only its own type, so
 * four of these sit cleanly in the backlog filter popover's grid. Each emits the full selection for its
 * type; the parent stitches the four back into one entityFilters list.
 */
const BacklogEntitySearch = ({
  entityType,
  label,
  selected,
  onChange,
  required,
  organizationalUnitIds
}: BacklogEntitySearchProps) => {
  'use memo';
  const [query, setQuery] = useState('');
  const [debouncedQuery] = useDebounceValue(query, 300);

  const hasOrganizationalUnitNarrowing = !!organizationalUnitIds?.length;

  const { data = [] } = queries.useFindPlanningEntities(
    debouncedQuery,
    // The endpoint takes a comma-separated string of types, not an array — one type here. The generated
    // client types it as an array, hence the cast.
    entityType as unknown as PlanningEntityType[],
    BACKLOG_ENTITY_PAGE_SIZE,
    SCHEDULING_LABEL as unknown as SchedulingLabel[],
    // Undefined rather than an empty array when nothing is picked: the generated query key is built from
    // these arguments, and undefined and [] would be two keys for the same tenant-wide search.
    organizationalUnitIds?.length ? organizationalUnitIds : undefined,
    // With a plan unit picked the field opens on that unit's people, so it asks before anything is
    // typed. Without one it stays a search — a blank unbounded term is every person in the tenant,
    // which the backend answers with nothing rather than with the tenant.
    { enabled: !!debouncedQuery || hasOrganizationalUnitNarrowing }
  );

  const getOptionLabel = (option: BacklogEntityFilter) =>
    formatEntityDisplayLabel(option.label, option.number);

  const options: BacklogEntityFilter[] = data
    .filter((entity) => !!entity.id)
    .map((entity) => ({
      entityId: entity.id as string,
      planningEntityType: entityType,
      label: entity.label ?? '',
      number: entity.number
    }));

  return (
    <BacklogFilterMultiSelect<BacklogEntityFilter>
      label={label}
      options={options}
      selected={selected}
      onChange={onChange}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(option, picked) =>
        option.entityId === picked.entityId
      }
      required={required}
      // This field fetches for the typed term, so the control must keep what the server returned.
      onQueryChange={setQuery}
    />
  );
};

export default BacklogEntitySearch;
